import { FC } from 'react';
import { createUseStyles } from 'react-jss';
import { CircleCheckIcon, IconComponent, PaperclipIcon, WarningIcon } from '../../theme/Icons';
import { IThemeColor } from '../../theme/IThemeColor';
import { ICriticalInfoDetail, ICriticalInfoItem } from './CriticalInfoPresenter';

const useStyles = createUseStyles({
    item: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 5
    },

    value: {
        fontSize: 15.5,
        fontWeight: 600,
        lineHeight: 1.32
    },

    detail: {
        fontSize: 13,
        lineHeight: 1.42,
        color: 'var(--ion-color-step-650)'
    },

    // a rounded rect rather than a pill: "Redetermination overdue since Aug 14, 2026" wraps to two
    // lines on a phone, and a pill that wraps looks broken
    chip: {
        display: 'inline-flex',
        alignItems: 'flex-start',
        gap: 6,
        width: 'fit-content',
        maxWidth: '100%',
        padding: '5px 9px 5px 8px',
        borderRadius: 8,
        fontSize: 12,
        fontWeight: 600,
        lineHeight: 1.35,
        textDecoration: 'none'
    },

    // colour alone would fail a colour-blind reader and get lost in a stack of grey lines, so
    // every chip carries an icon and a shape too
    chipIcon: {
        display: 'flex',
        flex: 'none',
        marginTop: 2
    },

    warningChip: {
        color: 'var(--ion-color-danger-shade)',
        backgroundColor: 'rgba(var(--ion-color-danger-rgb), 0.1)'
    },

    confirmedChip: {
        color: 'var(--ion-color-success-shade)',
        backgroundColor: 'rgba(var(--ion-color-success-rgb), 0.1)'
    },

    documentChip: {
        color: 'var(--ion-color-primary)',
        backgroundColor: 'rgba(var(--ion-color-primary-rgb), 0.07)',
        border: '1px solid rgba(var(--ion-color-primary-rgb), 0.18)'
    }
});

const chipsByTone: Record<NonNullable<ICriticalInfoDetail['tone']>, { className: 'warningChip' | 'confirmedChip'; Icon: IconComponent; color: IThemeColor }> = {
    warning: { className: 'warningChip', Icon: WarningIcon, color: 'danger' },
    confirmed: { className: 'confirmedChip', Icon: CircleCheckIcon, color: 'success' }
};

const CriticalInfoDetailView: FC<{ detail: ICriticalInfoDetail }> = ({ detail }) => {
    const classes = useStyles();

    if (detail.href) {
        return (
            <a
                className={`${classes.chip} ${classes.documentChip} mp-mask`}
                href={detail.href}
                target="_blank"
                rel="noreferrer"
                data-type="critical-info-document"
            >
                <span className={classes.chipIcon}><PaperclipIcon color="primary" customSize={12} /></span>
                {detail.text}
            </a>
        );
    }

    if (detail.tone) {
        const { className, Icon, color } = chipsByTone[detail.tone];

        return (
            <div
                className={`${classes.chip} ${classes[className]} mp-mask`}
                data-type="critical-info-detail"
                data-tone={detail.tone}
            >
                <span className={classes.chipIcon}><Icon color={color} customSize={12} /></span>
                {detail.text}
            </div>
        );
    }

    return (
        <div className={`${classes.detail} mp-mask`} data-type="critical-info-detail">{detail.text}</div>
    );
};

export interface ICriticalInfoItemViewProps {
    item: ICriticalInfoItem;
};

/**
 * One recorded fact and everything we know about how trustworthy it is. The details carry the
 * meaning a case manager opens the section for, so a verified date and a lapsed one read as chips
 * rather than as two more grey lines under the value.
 */
export const CriticalInfoItemView: FC<ICriticalInfoItemViewProps> = ({ item }) => {
    const classes = useStyles();

    return (
        <div className={classes.item}>
            <div className={`${classes.value} mp-mask`} data-type="critical-info-value">
                {item.text}
            </div>

            {item.details.map((detail, idx) => (
                <CriticalInfoDetailView key={idx} detail={detail} />
            ))}
        </div>
    );
};
